import axios from 'axios';
import { io } from 'socket.io-client';
import { differenceInCalendarDays, format, isValid, parseISO } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

import config from '../config';
import { decodeToken } from './jwtUtils';

// The licence is judged at login only, from the token the login API returns, so a licence
// that lapses mid-shift never interrupts a live call; it takes effect at the next login.

export const LICENSE_EXPIRED = 'LICENSE_EXPIRED';
export const LICENSE_WARNING_DAYS = 7;
// What the login APIs answer (HTTP 400) once the server's own licence check fails on expiry.
export const SERVER_LICENSE_EXPIRED_MESSAGE = 'License Expired!';

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

// Calendar days from today to the expiry date in the tenant's timezone; 0 or less means
// expired. Null when there is no usable answer (no date, bad date, bad zone), so bad data
// never blocks a login.
export const getLicenseDaysLeft = (user) => {
  const value = user?.license_expiry;
  if (!value) return null;

  const parsed = parseISO(String(value));
  if (!isValid(parsed)) return null;

  // Same fallback the rest of the app uses for the tenant's zone.
  const timezone =
    user?.time_zone_details?.zone_name || process.env.REACT_APP_TIMEZONE || 'Asia/Kolkata';
  // A date-only value is already a calendar day; a timestamp is read in the tenant's zone.
  const expiry = DATE_ONLY.test(String(value)) ? parsed : toZonedTime(parsed, timezone);
  const days = differenceInCalendarDays(expiry, toZonedTime(new Date(), timezone));
  return Number.isFinite(days) ? days : null;
};

// Warning colour for 1-7 days left: 7 green, 4-6 amber, the last 3 red. Bootstrap tone names.
export const getLicenseWarningTone = (daysLeft) => {
  if (daysLeft <= 3) return 'danger';
  if (daysLeft <= 6) return 'warning';
  return 'success';
};

export const formatLicenseDate = (value) => {
  const parsed = value ? parseISO(String(value)) : null;
  return parsed && isValid(parsed) ? format(parsed, 'dd/MM/yyyy') : '';
};

// Agents are only marked logged out by the socket server's user_logout handler. A one-shot
// connection rather than the app socket, which stays disconnected after an in-app logout.
// No reconnection: a logout that cannot be delivered now must never flush later, over a
// valid login.
const notifyAgentLogout = (user) => {
  const logoutSocket = io(config.SOCKET_URL, { transports: ['websocket'], reconnection: false });
  logoutSocket.once('connect', () => {
    logoutSocket.emit('user_logout', {
      user_id: user?.user_id,
      process_id: user?.process_details?.process_id,
      company_id: user?.companyId,
      type: user?.type
    });
    logoutSocket.disconnect();
  });
  logoutSocket.once('connect_error', () => logoutSocket.disconnect());
};

// Ends the session the login API just opened. Plain axios on purpose: apiInstance reads the
// stored token, and none is stored for this login, so its 401 handling would reload the
// login page out from under the licence alert.
const revokeLogin = async (token, user) => {
  try {
    await axios.post(
      '/auth/logout',
      {},
      { baseURL: config.BASE_URL, headers: { Authorization: `Bearer ${token}` } }
    );
  } catch (error) {
    console.log('error', error);
  }
  if (user?.type === 'User') notifyAgentLogout(user);
};

// Wraps a login request inside its thunk, before the fulfilled reducer stores the token.
// Rejecting here keeps the token out of the store, so AuthLayout never redirects into the app.
export const loginWithLicenseCheck = async (request, rejectWithValue) => {
  let response;
  try {
    response = await request();
  } catch (error) {
    if (error?.response?.data?.message === SERVER_LICENSE_EXPIRED_MESSAGE) {
      return rejectWithValue({ code: LICENSE_EXPIRED });
    }
    throw error;
  }

  const token = response?.token;
  const user = token ? decodeToken(token) : null;
  const daysLeft = getLicenseDaysLeft(user);
  if (daysLeft === null || daysLeft > 0) return response;

  await revokeLogin(token, user);
  return rejectWithValue({ code: LICENSE_EXPIRED, license_expiry: user?.license_expiry });
};
